/**
 * Captures console output and global errors for the super-user Debug modal.
 * Installed once at app startup; logs are retained since launch (ring buffer).
 */

const MAX_ENTRIES = 500;

/** @type {{ id: string, ts: string, level: string, text: string }[]} */
const entries = [];

/** @type {Set<() => void>} */
const listeners = new Set();

let installed = false;

function notifyListeners() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

function formatArg(arg) {
  if (arg == null) return String(arg);
  if (arg instanceof Error) {
    const stack = arg.stack ? `\n${arg.stack}` : '';
    return `${arg.name}: ${arg.message}${stack}`;
  }
  if (typeof arg === 'object') {
    try {
      return JSON.stringify(arg, null, 2);
    } catch {
      return String(arg);
    }
  }
  return String(arg);
}

function pushEntry(level, args) {
  const text = args.map(formatArg).join(' ');
  entries.push({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    ts: new Date().toISOString(),
    level,
    text,
  });
  while (entries.length > MAX_ENTRIES) {
    entries.shift();
  }
  notifyListeners();
}

export function getConsoleEntries() {
  return [...entries];
}

export function clearConsoleEntries() {
  entries.length = 0;
  notifyListeners();
}

export function subscribeConsoleCapture(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function installConsoleCapture() {
  if (installed) return;
  installed = true;

  pushEntry('info', ['Console capture started']);

  ['log', 'info', 'warn', 'error', 'debug'].forEach((level) => {
    const original = console[level]?.bind(console);
    if (!original) return;
    console[level] = (...args) => {
      pushEntry(level, args);
      original(...args);
    };
  });

  if (typeof global !== 'undefined' && global.ErrorUtils?.getGlobalHandler) {
    const previous = global.ErrorUtils.getGlobalHandler();
    global.ErrorUtils.setGlobalHandler((error, isFatal) => {
      pushEntry('error', [
        isFatal ? '[Fatal JS Error]' : '[JS Error]',
        error?.message ?? error,
        error?.stack,
      ]);
      previous?.(error, isFatal);
    });
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('error', (event) => {
      pushEntry('error', [
        '[Uncaught Error]',
        event.message,
        event.filename ? `${event.filename}:${event.lineno}:${event.colno}` : '',
        event.error?.stack,
      ]);
    });
    window.addEventListener('unhandledrejection', (event) => {
      pushEntry('error', ['[Unhandled Promise Rejection]', event.reason]);
    });
  }
}

// Auto-install when module loads (before other app code logs).
installConsoleCapture();
